import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAccountsTree } from '../client';
import { flattenAccounts } from '../utils/account-tree';
import type { AccountNode } from '../schemas';
import { Input } from '../../../components/ui/FormField';
import { Button } from '../../../components/ui/Button';

interface AccountSearchInputProps {
  value: string;
  onChange: (accountId: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function AccountSearchInput({ value, onChange, disabled, placeholder = 'Search account...' }: AccountSearchInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { data: accountsTree } = useAccountsTree();
  
  const flatAccounts = useMemo(() => flattenAccounts(accountsTree), [accountsTree]);
  
  // Find selected account to display
  const selectedAccount = useMemo(() => 
    flatAccounts.find(a => a.id === value), 
    [flatAccounts, value]
  );

  const filteredAccounts = useMemo(() => {
    if (!searchQuery.trim()) return flatAccounts.slice(0, 50);
    const query = searchQuery.toLowerCase();
    return flatAccounts
      .filter(a => 
        a.code.toLowerCase().includes(query) || 
        a.name.toLowerCase().includes(query)
      )
      .slice(0, 50);
  }, [flatAccounts, searchQuery]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node) &&
          dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    setIsOpen(true);
    // If they clear the input, also clear the selected value
    if (!newValue) {
      onChange('');
    }
  };

  const handleSelectAccount = (account: AccountNode) => {
    onChange(account.id);
    setSearchQuery(`${account.code} - ${account.name}`);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const displayValue = selectedAccount 
    ? `${selectedAccount.code} - ${selectedAccount.name}` 
    : searchQuery;

  return (
    <div className="relative" style={{ width: '100%' }}>
      <Input
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full"
        autoComplete="off"
      />
      {isOpen && !disabled && (
        <div 
          ref={dropdownRef}
          role="listbox"
          className="absolute z-10 w-full mt-1 bg-surface-0 border border-surface-3 rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {filteredAccounts.length === 0 ? (
            <div className="px-3 py-2 text-sm text-text-muted">No accounts found</div>
          ) : (
            filteredAccounts.map((account) => (
              <Button
                key={account.id}
                type="button"
                variant="ghost"
                onClick={() => handleSelectAccount(account)}
                className={`w-full h-auto justify-start rounded-none text-left px-3 py-2 text-sm font-normal ${
                  value === account.id ? 'bg-accent-bg' : ''
                }`}
              >
                <span className="font-mono text-text-secondary mr-2">{account.code}</span>
                <span className="text-text-primary">{account.name}</span>
                <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-surface-2 text-text-muted">
                  {account.type}
                </span>
                {!account.isActive && (
                  <span className="ml-2 text-xs text-danger-600">(Inactive)</span>
                )}
              </Button>
            ))
          )}
        </div>
      )}
    </div>
  );
}